"use client";

import { Bell, Download } from "lucide-react";
import { Link } from "react-router-dom";

import { Popover } from "@/components/ui-system";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/modules/auth/store";
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotificationsQuery, useNotificationStream } from "@/modules/notifications/hooks";

function readPayload(payload: Record<string, unknown> | null, key: string): string {
  const value = payload?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function formatRelative(dateStr: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
}

function severityClass(type: string, payload: Record<string, unknown> | null, isRead: boolean): string {
  if (type !== "alert_triggered") return isRead ? "" : "bg-accent-muted";
  const severity = readPayload(payload, "severity").toLowerCase();
  if (severity === "critical") return "bg-status-error-surface";
  if (severity === "warning") return "bg-status-warning-surface";
  if (severity === "info") return "bg-status-info-surface";
  return isRead ? "" : "bg-accent-muted";
}

export function NotificationCenter() {
  const token = useAuthStore((state) => state.token);
  const tenantId = useAuthStore((state) => state.tenantId);
  const enabled = Boolean(token && tenantId);
  const query = useNotificationsQuery({ limit: 5 }, enabled);
  const markAllRead = useMarkAllNotificationsRead();
  const markRead = useMarkNotificationRead();
  const items = query.data?.items ?? [];
  const unread = query.data?.unread_count ?? 0;
  useNotificationStream(enabled);

  return (
    <Popover
      placement="bottom-end"
      ariaLabel="Notifications"
      contentClassName="max-h-[min(32rem,calc(100vh-6rem))] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto p-0"
      trigger={
        <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-control border border-card-border bg-surface-primary text-muted shadow-raised transition-colors hover:bg-surface-hover hover:text-foreground">
          <Bell className="h-4 w-4" aria-hidden />
          {unread > 0 ? <span className="absolute right-0 top-0 flex h-5 min-w-5 translate-x-1/4 -translate-y-1/4 items-center justify-center rounded-full border-2 border-surface-primary bg-accent px-1 text-micro font-bold leading-none text-accent-foreground shadow-raised">{unread > 9 ? "9+" : unread}</span> : null}
        </span>
      }
    >
      {(close) => (
        <>
          <div className="flex items-center justify-between border-b border-card-border px-ds-4 py-ds-3">
            <div><p className="text-caption font-semibold">Notifications</p><p className="text-micro text-muted">{unread} unread</p></div>
            {unread > 0 ? <button type="button" disabled={markAllRead.isPending} onClick={() => void markAllRead.mutateAsync()} className="text-micro font-semibold text-accent hover:underline disabled:opacity-50">Mark all read</button> : null}
          </div>
          <div className="p-ds-2">
            {query.isLoading ? <p className="px-ds-3 py-ds-5 text-center text-caption text-muted">Loading notifications…</p> : items.length === 0 ? <p className="px-ds-3 py-ds-5 text-center text-caption text-muted">No notifications</p> : items.map((notification) => {
              const deviceName = readPayload(notification.payload, "device_name") || readPayload(notification.payload, "device_uid");
              const serial = readPayload(notification.payload, "device_serial_number");
              const downloadUrl = notification.type === "backup" ? readPayload(notification.payload, "download_url") || notification.link || "" : "";
              const href = notification.link ?? (notification.alert_id ? `/alerts/history/${notification.alert_id}` : "/notifications");
              const mark = () => {
                if (!notification.is_read) void markRead.mutateAsync(notification.id);
                close();
              };
              return (
                <article key={notification.id} className={cn("rounded-control px-ds-3 py-ds-3 transition-colors hover:bg-surface-hover", severityClass(notification.type, notification.payload, notification.is_read))}>
                  <div className="flex items-start gap-ds-3">
                    <span className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", notification.is_read ? "bg-status-offline" : "bg-accent")} aria-hidden />
                    <div className="min-w-0 flex-1">
                      <Link to={href} onClick={mark} className="block truncate text-caption font-semibold text-foreground hover:text-accent">{notification.title}</Link>
                      {notification.body ? <p className="mt-ds-1 line-clamp-2 text-micro text-muted">{notification.body}</p> : null}
                      {deviceName ? <p className="mt-ds-1 truncate text-micro font-medium text-foreground">{deviceName}{serial && serial !== deviceName ? <span className="text-muted"> · {serial}</span> : null}</p> : null}
                      <p className="mt-ds-1 text-micro uppercase tracking-[0.06em] text-muted">{notification.is_read ? "Read" : "Unread"} · {formatRelative(notification.created_at)}</p>
                      {downloadUrl ? <Link to={downloadUrl} target="_blank" rel="noreferrer" onClick={mark} className="mt-ds-2 inline-flex min-h-8 items-center gap-ds-1 rounded-control bg-accent px-ds-3 text-micro font-semibold text-accent-foreground"><Download className="h-3.5 w-3.5" />Download</Link> : null}
                    </div>
                    {!notification.is_read ? <button type="button" className="text-micro font-semibold text-accent hover:underline" onClick={() => void markRead.mutateAsync(notification.id)}>Read</button> : null}
                  </div>
                </article>
              );
            })}
          </div>
          <div className="border-t border-card-border p-ds-2"><Link to="/notifications" onClick={close} className="flex min-h-9 items-center justify-center rounded-control text-caption font-semibold text-accent hover:bg-accent-muted">View all notifications</Link></div>
        </>
      )}
    </Popover>
  );
}
